import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { catchError, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { IUser } from '../models/other/user.interface';
import { TranslateService } from './translate.service';
import {
  IRecordDataModel
} from '../components/template-components/common/convert-legacy-template/convert-legacy-template.component';
import { SearchDataRequest } from '../models/template/search-data-request.interface';
import { ChannelSearchResult } from '../models/template/channel-search-result.interface';
import { TypeEnum } from '../models/template/type.enum';
import { TemplateRequest } from '../models/template/template-request.interface';
import { GetTemplateRequest } from '../models/template/get-template-request.interface';
import { UpdatePlannedDateRequest } from '../models/template/update-planned-date-request.interface';

@Injectable({
  providedIn: 'root',
})
export class TemplateService implements OnDestroy {
  isTranslator: boolean | undefined;

  private _destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private translateService: TranslateService
  ) {
    this.translateService.isTranslator$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.isTranslator = res;
        },
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private getHostUrl = () => {
    const url = environment.scheme + environment.host + environment.port + '/';
    return url;
  };

  private readonly getTemplateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/getTemplate`;

  private readonly getTemplateByIdUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/getTemplateById`;

  private readonly getTemplateListUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/getTemplateList`;

  private readonly deleteTemplateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/deleteTemplate`;

  private readonly createTemplateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/createTemplate`;

  private readonly updateTemplateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/updateTemplate`;

  private readonly updateLockedByUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/lockTemplate`;

  private readonly uploadImageUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/uploadImage`;

  private readonly uploadVideoUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/uploadVideo`;

  private readonly deleteMediaUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/deleteMedia`;

  private readonly deleteMediaWithinABlockUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/deleteMediaWithinABlock`;

  private readonly searchTemplateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/searchTemplate`;

  private readonly searchTemplatesAcrossChannelsUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/searchTemplatesAcrossChannels`;

  private readonly copyTemplateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/copyTemplate`;

  private readonly getResponsysTable = `${this.getHostUrl()}api/v${environment.version
  }/Responsys/getResponsysTable`;

  private readonly convertTemplateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/convertTemplate`;

  private readonly updatePlannedDateUrl = `${this.getHostUrl()}api/v${environment.version
  }/Template/updatePlannedDate`;


  getTemplateList() {
    return this.http.get<any>(this.getTemplateListUrl);
  }

  createTemplate(templateRequest: TemplateRequest): Observable<any> {
    // console.log('Create->', templateRequest);
    return this.http.post<any>(this.createTemplateUrl, templateRequest);
  }

  //todo:: napravi interface za templateRequest
  updateTemplate(templateRequest: TemplateRequest): Observable<any> {
    return this.http.put<any>(this.updateTemplateUrl, templateRequest);
  }

  getTemplate(getTemplateRequest: GetTemplateRequest): Observable<any> {
    const params = new HttpParams()
      .set('templateName', getTemplateRequest.templateName)
      .set('templateType', getTemplateRequest.templateType)
      .set('language', getTemplateRequest.language);
    return this.http.get<any>(this.getTemplateUrl, {params});
  }

  getTemplateById(templateId: string): Observable<any> {
    const params = new HttpParams().set('templateId', templateId);
    return this.http.get<any>(this.getTemplateByIdUrl, {params});
  }

  deleteTemplate(templateName: string | null) {
    if (!templateName) {
      templateName = '';
    }
    const params = new HttpParams().set('templateName', templateName);
    return this.http.delete<any>(this.deleteTemplateUrl, {params});
  }

  sendLockBy(templateId: string, user?: IUser | null) {
    const url = `${this.updateLockedByUrl}/${encodeURIComponent(templateId)}`;

    const params = this.translateService.setTranslatorParams(
      this.isTranslator!
    );

    return this.http.put<any>(url, user, {params});
  }

  uploadImage(
    file: File,
    templateName: string,
    blockId: string
  ): Observable<string> {
    const formData = new FormData();
    formData.append('image', file, file.name);
    formData.append('templateName', templateName);
    formData.append('blockId', blockId);

    return this.http.post<string>(this.uploadImageUrl, formData);
  }

  uploadVideo(
    file: File,
    templateName: string,
    blockId: string
  ): Observable<string> {
    const formData = new FormData();
    formData.append('video', file, file.name);
    formData.append('templateName', templateName);
    formData.append('blockId', blockId);

    return this.http.post<string>(this.uploadVideoUrl, formData);
  }

  deleteMedia(mediaURI: string): any {
    const encoded = encodeURIComponent(mediaURI);
    return this.http.delete<string>(
      `${this.deleteMediaUrl}/mediaURI=${encoded}`
    );
  }

  deleteMediaWithinABlock(
    folderName: string,
    blockId: string
  ): Observable<string> {
    return this.http.delete<string>(
      `${this.deleteMediaWithinABlockUrl}/folderName=${folderName}&blockId=${blockId}`
    );
  }

  searchTemplate(searchData: SearchDataRequest): Observable<string[]> {
    const params = {
      searchTerm: searchData.searchTerm,
      templateType: searchData.templateType
    };
    return this.http.get<string[]>(this.searchTemplateUrl, {params});
  }

  /**
   * RCA-487: searches templates by name across every channel at once. Omit
   * `templateType` to get matches for every channel; pass it to scope the
   * matches to one channel while still getting counts for all of them.
   */
  searchTemplatesAcrossChannels(searchTerm: string, templateType?: TypeEnum): Observable<ChannelSearchResult> {
    let params = new HttpParams().set('searchTerm', searchTerm);
    if (templateType) {
      params = params.set('templateType', templateType);
    }
    return this.http.get<ChannelSearchResult>(this.searchTemplatesAcrossChannelsUrl, { params });
  }

  copyTemplate(request: TemplateRequest, originalTemplateName: string): Observable<any> {
    return this.http
      .post<any>(this.copyTemplateUrl, {
        request,
        originalTemplateName,
      })
  }

  getLegacyTemplate(formValue: { templateFolder: string; templateName: string; language: string }): Observable<any> {
    let httpParams = new HttpParams()
      .set('folder', formValue.templateFolder ?? '')
      .set('tableName', formValue.templateName ?? '')
      .set('lang', formValue.language ?? '');

    return this.http.get<any>(this.getResponsysTable, {params: httpParams});
  }

  convertTemplate(recordData: IRecordDataModel, templateName: string, language: string): Observable<any> {
    console.log("convert Template", recordData);
    const payload: any = {
      recordData,                 // tvoj postojeći objekt IRecordDataModel
      templateName: templateName,
      language: language,
    };
    console.log("convertTemplate payload", payload);

    return this.http
      .post<any>(this.convertTemplateUrl,
        payload,
      )
  }

  //todo:: napravi interface za templateRequest
  updatePlannedDate(updatePlannedDateRequest: UpdatePlannedDateRequest): Observable<any> {
    return this.http.put<any>(this.updatePlannedDateUrl, updatePlannedDateRequest);
  }
}
